import React, { useMemo } from 'react';
import { Card, Typography, Row, Col, Space, Empty, Button } from 'antd';
import { ThunderboltOutlined, CloudOutlined, ArrowUpOutlined, CloseOutlined } from '@ant-design/icons';
import type { ForecastRain } from '../../api/forecastService';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useMinuteClock } from '../../hooks/useMinuteClock';
import { usePermissions } from '../../hooks/usePermissions';
import { resolveActiveRainForecast } from '../../utils/rainForecastSelection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

interface RainForecastCardProps {
  forecasts: ForecastRain[];
  selectedDate: Date | null;
  onClose?: () => void;
}

const getRainTypeLabel = (rainType: number | null): string => {
  if (rainType === null) return 'Unknown';
  switch (rainType) {
    case 0:
      return 'No rain';
    case 1:
      return 'Rain expected';
    case 2:
      return 'Raining';
    default:
      return 'Unknown';
  }
};

const getRainTypeIcon = (rainType: number | null, fewDrops: boolean | null) => {
  if (fewDrops) {
    return <CloudOutlined style={{ fontSize: 16 }} />;
  }
  
  if (rainType === null || rainType === 0) {
    return <CloudOutlined style={{ fontSize: 16 }} />;
  }
  
  return <ThunderboltOutlined style={{ fontSize: 16 }} />;
};

export const RainForecastCard: React.FC<RainForecastCardProps> = ({ forecasts, selectedDate, onClose }) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const isDark = theme === 'dark';
  const tz = selectedEvent?.circuit?.time_zone || 'UTC';
  const now = useMinuteClock();
  const { isRoot } = usePermissions();

  const activeForecast = useMemo(
    () => {
      if (!selectedEvent) return null;
      return resolveActiveRainForecast({
        forecasts,
        anchorTime: selectedDate,
        sessions: selectedEvent.sessions ?? [],
        circuitTimezone: tz,
        requireActive: true,
        isRoot,
        currentTime: now,
      });
    },
    [forecasts, selectedDate, selectedEvent, tz, isRoot, now],
  );

  const cardStyle = {
    background: isDark ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(10px)',
  };

  const headerStyle: React.CSSProperties = {
    borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    padding: '8px 14px 6px 14px',
    marginBottom: 0,
    cursor: 'grab',
    userSelect: 'none' as const,
  };

  if (!activeForecast) {
    return (
      <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
        <div className="drag-handle" style={headerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              Rain Forecast
            </span>
            {onClose && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
                style={{ 
                  color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                  minWidth: 24,
                  padding: '0 4px',
                }}
              />
            )}
          </div>
        </div>
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ fontSize: 11 }}>No rain forecast data available</span>}
          />
        </div>
      </Card>
    );
  }

  const updatedTime = dayjs.utc(activeForecast.value_date).tz(tz);

  return (
    <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
      <div className="drag-handle" style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            Rain Forecast @ {updatedTime.format('HH:mm')}
          </span>
          {onClose && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={onClose}
              style={{ 
                color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                minWidth: 24,
                padding: '0 4px',
              }}
            />
          )}
        </div>
      </div>

      <div style={{ padding: '10px 14px' }}>
        <Row gutter={[12, 8]} align="middle">
          {/* Rain Status Icon */}
          <Col xs={24} sm={6} style={{ textAlign: 'center' }}>
            <Space orientation="vertical" size={4} style={{ width: '100%' }}>
              {getRainTypeIcon(activeForecast.rain_type, activeForecast.few_drops)}
              <Text strong style={{ fontSize: 11, fontWeight: 600 }}>
                {activeForecast.few_drops ? 'Drops at times' : getRainTypeLabel(activeForecast.rain_type)}
              </Text>
            </Space>
          </Col>

          {/* Wind Direction & Speed */}
          {(activeForecast.rain_direction_deg !== null || activeForecast.rain_speed_kph !== null) && (
            <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
              <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                <div style={{ fontSize: 10, fontWeight: 500 }}>
                  <Text>Direction</Text>
                </div>
                {activeForecast.rain_direction_deg !== null ? (
                  <div>
                    <ArrowUpOutlined
                      style={{
                        fontSize: 16,
                        transform: `rotate(${activeForecast.rain_direction_deg}deg)`,
                      }}
                    />
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                      <Text strong>{activeForecast.rain_direction_deg}°</Text>
                    </div>
                  </div>
                ) : (
                  <Text style={{ fontSize: 11 }}>-</Text>
                )}
              </Space>
            </Col>
          )}

          {activeForecast.rain_speed_kph !== null && (
            <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
              <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                <div style={{ fontSize: 10, fontWeight: 500 }}>
                  <Text>Speed</Text>
                </div>
                <div>
                  <Text strong style={{ fontSize: 14 }}>
                    {activeForecast.rain_speed_kph}
                  </Text>
                  <Text style={{ fontSize: 10, marginLeft: 2 }}>
                    kph
                  </Text>
                </div>
              </Space>
            </Col>
          )}

          {/* Comments */}
          <Col xs={24} sm={activeForecast.rain_direction_deg === null && activeForecast.rain_speed_kph === null ? 18 : 12}>
            <Text strong style={{ fontSize: 11, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {activeForecast.message}
            </Text>
          </Col>
        </Row>

        {/* Additional rain timing info */}
        {(activeForecast.rain_start_time || activeForecast.rain_end_time) && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` }}>
            <Row gutter={[8, 4]}>
              {activeForecast.rain_start_time && (
                <Col span={12}>
                  <Text style={{ fontSize: 10 }}>Start: </Text>
                  <Text strong style={{ fontSize: 10 }}>{activeForecast.rain_start_time}</Text>
                </Col>
              )}
              {activeForecast.rain_end_time && (
                <Col span={12}>
                  <Text style={{ fontSize: 10 }}>End: </Text>
                  <Text strong style={{ fontSize: 10 }}>{activeForecast.rain_end_time}</Text>
                </Col>
              )}
            </Row>
          </div>
        )}

        {/* Rain pattern and intensity */}
        {(activeForecast.rain_pattern || activeForecast.rain_intensity !== null) && (
          <div style={{ marginTop: 8 }}>
            <Row gutter={[8, 4]}>
              {activeForecast.rain_pattern && (
                <Col span={12}>
                  <Text style={{ fontSize: 10 }}>Pattern: </Text>
                  <Text strong style={{ fontSize: 10 }}>{activeForecast.rain_pattern}</Text>
                </Col>
              )}
              {activeForecast.rain_intensity !== null && (
                <Col span={12}>
                  <Text style={{ fontSize: 10 }}>Intensity: </Text>
                  <Text strong style={{ fontSize: 10 }}>{activeForecast.rain_intensity}</Text>
                </Col>
              )}
            </Row>
          </div>
        )}
      </div>
    </Card>
  );
};

